import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { setLogoutReason } from '../lib/logout-reason';

interface AutoLogoutOptions {
  /** Total idle time before automatic logout in milliseconds (default: 15 minutes = 900,000ms) */
  idleTimeoutMs?: number;
  /** Inactivity warning countdown duration in milliseconds (default: 60 seconds = 60,000ms) */
  warningTimeMs?: number;
  /** Storage key to synchronize activity across browser tabs */
  storageKey?: string;
}

export function useAutoLogout({
  idleTimeoutMs = 15 * 60 * 1000, // 15 minutes
  warningTimeMs = 60 * 1000, // 60 seconds
  storageKey = 'lms_admin_last_active',
}: AutoLogoutOptions = {}) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const logout = useAuthStore(state => state.logout);

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(Math.ceil(warningTimeMs / 1000));

  const lastActivityRef = useRef<number>(Date.now());
  const isThrottledRef = useRef<boolean>(false);

  // Sync latest activity timestamp
  const recordActivity = useCallback(() => {
    if (!isAuthenticated) return;
    const now = Date.now();
    lastActivityRef.current = now;

    if (!isThrottledRef.current) {
      isThrottledRef.current = true;
      try {
        localStorage.setItem(storageKey, String(now));
      } catch {
        // Storage might fail if private mode / quota exceeded
      }
      setTimeout(() => {
        isThrottledRef.current = false;
      }, 1000);
    }
  }, [isAuthenticated, storageKey]);

  // Stay logged in button click / user action during warning
  const stayLoggedIn = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    try {
      localStorage.setItem(storageKey, String(now));
    } catch {
      // ignore
    }
    setIsWarningOpen(false);
    setCountdown(Math.ceil(warningTimeMs / 1000));
  }, [storageKey, warningTimeMs]);

  // Immediate logout action
  const performLogout = useCallback(() => {
    setLogoutReason('idle_timeout');
    logout();
    queryClient.clear();
    setIsWarningOpen(false);
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }, [logout, queryClient]);

  // Main timer loop & event listeners
  useEffect(() => {
    if (!isAuthenticated) {
      setIsWarningOpen(false);
      return;
    }

    // Initialize last active from localStorage if newer
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > lastActivityRef.current) {
          lastActivityRef.current = parsed;
        }
      }
    } catch {
      // ignore
    }

    // Activity event listeners (mousedown, click, keydown, scroll, touchstart - no mousemove to save CPU)
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handleUserEvent = () => recordActivity();

    events.forEach(eventName => {
      window.addEventListener(eventName, handleUserEvent, { passive: true });
    });

    // Multi-tab sync via storage event
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        const remoteTime = parseInt(e.newValue, 10);
        if (!isNaN(remoteTime) && remoteTime > lastActivityRef.current) {
          lastActivityRef.current = remoteTime;
          setIsWarningOpen(false);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Tab visibility change (e.g. user resumes from background tab after hours)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        try {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed)) {
              lastActivityRef.current = parsed;
            }
          }
        } catch {
          // ignore
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check interval every 1 second
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;

      if (elapsed >= idleTimeoutMs) {
        performLogout();
      } else if (elapsed >= idleTimeoutMs - warningTimeMs) {
        const remaining = Math.max(0, Math.ceil((idleTimeoutMs - elapsed) / 1000));
        setIsWarningOpen(true);
        setCountdown(remaining);
      } else {
        if (isWarningOpen) {
          setIsWarningOpen(false);
        }
      }
    }, 1000);

    return () => {
      events.forEach(eventName => {
        window.removeEventListener(eventName, handleUserEvent);
      });
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [
    isAuthenticated,
    idleTimeoutMs,
    warningTimeMs,
    storageKey,
    recordActivity,
    performLogout,
    isWarningOpen,
  ]);

  return {
    isWarningOpen,
    countdown,
    stayLoggedIn,
    logoutNow: performLogout,
  };
}
